import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Package, Truck, Globe, MapPin, Phone, Mail, Shield, Clock, CheckCircle2, Plane, Building2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { Helmet } from 'react-helmet-async';

const ShippingCanadaToUAEPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', weight: '' });
  const { toast } = useToast();
  
  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  
  const handleSubmit = (e) => {
    e.preventDefault();
    toast({ 
      title: "Quote Request Received!", 
      description: "We'll contact you within 24 hours with shipping rates to UAE." 
    });
    setFormData({ name: '', email: '', phone: '', weight: '' });
  };

  const benefits = [
    {
      icon: Globe,
      title: "All Emirates Covered",
      description: "Ship to Dubai, Abu Dhabi, Sharjah, Ajman, Fujairah, Ras Al Khaimah, and Umm Al Quwain."
    },
    {
      icon: Truck,
      title: "Door-to-Door Delivery",
      description: "Convenient pickup from Canada and direct delivery to any address across the UAE."
    },
    {
      icon: Clock,
      title: "Express Shipping",
      description: "Fast delivery options with DHL, FedEx, and UPS. Express service available for urgent shipments."
    },
    {
      icon: Shield,
      title: "UAE Customs Expertise",
      description: "Professional assistance with UAE customs clearance and documentation requirements."
    }
  ];

  const services = [
    "Documents to Dubai & UAE",
    "Parcels and packages",
    "Personal belongings",
    "Business and commercial cargo",
    "E-commerce shipments",
    "Gift parcels",
    "Electronics and technology products",
    "Apparel and fashion items"
  ];

  const uaeCities = [
    { name: "Dubai", icon: Building2 },
    { name: "Abu Dhabi", icon: Building2 },
    { name: "Sharjah", icon: MapPin },
    { name: "Ajman", icon: MapPin },
    { name: "Fujairah", icon: MapPin },
    { name: "Ras Al Khaimah", icon: MapPin }
  ];

  return (
    <>
      <Helmet>
        <title>Shipping From Canada to Dubai & UAE | International Courier | Shippzy</title>
        <meta name="description" content="Reliable shipping from Canada to Dubai, Abu Dhabi, Sharjah, and all UAE emirates. Documents, parcels, business cargo. Door-to-door courier with customs clearance." />
        <link rel="canonical" href="https://www.shippzy.ca/shipping-canada-to-uae" />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section with Dubai-Inspired Design */}
        <section className="relative bg-gradient-to-br from-amber-900 via-orange-900 to-yellow-900 text-white py-24 overflow-hidden">
          {/* Dubai Skyline Silhouette Pattern */}
          <div className="absolute bottom-0 left-0 right-0 h-64 opacity-10">
            <svg viewBox="0 0 1200 200" className="w-full h-full" preserveAspectRatio="none">
              <rect x="50" y="80" width="40" height="120" fill="currentColor" />
              <rect x="100" y="60" width="60" height="140" fill="currentColor" />
              <polygon points="130,20 140,60 120,60" fill="currentColor" />
              <rect x="180" y="100" width="50" height="100" fill="currentColor" />
              <rect x="250" y="40" width="80" height="160" fill="currentColor" />
              <polygon points="290,10 305,40 275,40" fill="currentColor" />
              <rect x="350" y="90" width="45" height="110" fill="currentColor" />
              <rect x="420" y="70" width="70" height="130" fill="currentColor" />
              <rect x="510" y="85" width="55" height="115" fill="currentColor" />
            </svg>
          </div>

          {/* Animated Gold Particles */}
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(15)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 bg-yellow-400/30 rounded-full"
                animate={{
                  y: [-20, 800],
                  x: [Math.random() * 100, Math.random() * 100],
                  opacity: [0, 1, 0]
                }}
                transition={{
                  duration: 8 + Math.random() * 4,
                  repeat: Infinity,
                  delay: Math.random() * 3
                }}
                style={{
                  left: `${Math.random() * 100}%`,
                  top: -20
                }}
              />
            ))}
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <div className="inline-block mb-4 px-4 py-2 bg-yellow-600/30 border border-yellow-400/30 rounded-full">
                  <span className="text-yellow-300 text-sm font-semibold">🇦🇪 Canada to UAE Shipping</span>
                </div>
                <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                  Shipping from Canada
                  <span className="block bg-gradient-to-r from-yellow-300 via-amber-300 to-orange-300 bg-clip-text text-transparent">to Dubai & UAE</span>
                </h1>
                <p className="text-xl text-gray-200 mb-8">
                  Reliable international courier service to Dubai, Abu Dhabi, Sharjah, and all UAE emirates. Fast delivery with customs clearance support.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    className="bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-600 hover:to-amber-700 text-white px-8 py-6 text-lg shadow-xl"
                    onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}
                  >
                    Get Free Quote
                  </Button>
                  <Button 
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
                    onClick={() => window.location.href = '/contact'}
                  >
                    Contact Us
                  </Button>
                </div>
                <div className="mt-8 flex items-center gap-6 text-sm text-gray-300">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <span>+1 (437) 616-0758</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    <span>ship@shippzy.ca</span>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="hidden md:block"
              >
                <div className="relative">
                  <motion.div
                    animate={{ 
                      rotateY: [0, 360],
                    }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    className="bg-gradient-to-br from-yellow-600 via-amber-600 to-orange-600 rounded-3xl p-8 shadow-2xl relative"
                    style={{ transformStyle: 'preserve-3d' }}
                  >
                    <Package className="w-32 h-32 text-white mx-auto mb-4" />
                    <div className="text-center">
                      <div className="text-4xl font-bold text-white mb-2">4-8 Days</div>
                      <div className="text-yellow-100">Delivery to UAE</div>
                    </div>
                    {/* Gold accent stripes */}
                    <div className="absolute top-0 right-0 w-20 h-20 bg-yellow-400/30 rounded-bl-full"></div>
                    <div className="absolute bottom-0 left-0 w-16 h-16 bg-orange-400/30 rounded-tr-full"></div>
                  </motion.div>
                  <div className="absolute -bottom-4 -right-4 bg-amber-500 text-white px-6 py-3 rounded-full font-bold shadow-xl">
                    Save 75%
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Why Choose Shippzy for UAE Shipping?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Competitive rates, reliable carriers, and expert UAE customs support
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Card className="h-full hover:shadow-xl transition-shadow duration-300 border-t-4 border-amber-600">
                      <CardHeader>
                        <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        <CardTitle className="text-xl">{benefit.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-gray-600">{benefit.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-20 bg-gradient-to-br from-amber-50 to-orange-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-4">Shipping Rates from Canada to UAE/Dubai</h2>
              <p className="text-xl text-gray-600">Transparent pricing with no hidden fees</p>
            </motion.div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }}>
                <Card className="border-t-4 border-amber-600 h-full">
                  <CardHeader>
                    <CardTitle>Documents (Standard)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-amber-700 mb-2">CAD $44</div>
                    <p className="text-sm text-gray-600">7-9 business days</p>
                  </CardContent>
                </Card>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }}>
                <Card className="border-t-4 border-amber-600 h-full">
                  <CardHeader>
                    <CardTitle>Documents (Express)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-amber-700 mb-2">CAD $77</div>
                    <p className="text-sm text-gray-600">2-5 business days</p>
                  </CardContent>
                </Card>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.3 }}>
                <Card className="border-t-4 border-amber-600 h-full">
                  <CardHeader>
                    <CardTitle>1 kg Parcel</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-amber-700 mb-2">CAD $45</div>
                    <p className="text-sm text-gray-600">5-7 business days</p>
                  </CardContent>
                </Card>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.4 }}>
                <Card className="border-t-4 border-amber-600 h-full">
                  <CardHeader>
                    <CardTitle>5 kg Parcel</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-amber-700 mb-2">CAD $85</div>
                    <p className="text-sm text-gray-600">5-7 business days</p>
                  </CardContent>
                </Card>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.5 }}>
                <Card className="border-t-4 border-amber-600 h-full">
                  <CardHeader>
                    <CardTitle>10 kg Parcel</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-amber-700 mb-2">CAD $149</div>
                    <p className="text-sm text-gray-600">5-7 business days</p>
                  </CardContent>
                </Card>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.6 }}>
                <Card className="border-t-4 border-amber-600 h-full">
                  <CardHeader>
                    <CardTitle>15 kg Parcel</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-amber-700 mb-2">CAD $195</div>
                    <p className="text-sm text-gray-600">5-7 business days</p>
                  </CardContent>
                </Card>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.7 }}>
                <Card className="border-4 border-yellow-500 relative h-full">
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-yellow-500 text-white px-4 py-1 rounded-full text-sm font-bold">
                    BEST VALUE
                  </div>
                  <CardHeader>
                    <CardTitle>23 kg Slab</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-yellow-700 mb-2">CAD $9.99/kg</div>
                    <p className="text-sm text-gray-600">5-10 business days</p>
                  </CardContent>
                </Card>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.8 }}>
                <Card className="border-4 border-green-500 relative h-full">
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-4 py-1 rounded-full text-sm font-bold">
                    POPULAR
                  </div>
                  <CardHeader>
                    <CardTitle>30 kg Slab</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-green-700 mb-2">CAD $9.99/kg</div>
                    <p className="text-sm text-gray-600">5-10 business days</p>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.9 }}
              className="mt-8 bg-amber-50 border-l-4 border-amber-600 p-6 rounded-lg max-w-4xl mx-auto"
            >
              <h3 className="font-bold mb-3 text-lg">Heavy Parcel Pricing</h3>
              <div className="space-y-2">
                <p><span className="font-semibold">23 kg:</span> CAD $229.77 (exact)</p>
                <p><span className="font-semibold">30 kg:</span> CAD $299.70 (exact)</p>
                <p><span className="font-semibold">Additional Weight:</span> CAD $15/kg</p>
              </div>
              <p className="text-sm text-gray-600 mt-3">
                Additional weight is charged at $15/kg between the 23 kg and 30 kg slabs, and above the 30 kg slab.
              </p>
              <p className="text-sm text-gray-600 mt-2">Base prices. Taxes extra. Customs duties not included.</p>
            </motion.div>
          </div>
        </section>

        {/* Services & UAE Cities */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12">
              {/* Services */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Card className="h-full shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-2xl flex items-center gap-3">
                      <Package className="w-8 h-8 text-amber-600" />
                      What We Ship to UAE
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {services.map((service, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{service}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>

              {/* UAE Cities */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Card className="h-full shadow-lg border-l-4 border-amber-500">
                  <CardHeader>
                    <CardTitle className="text-2xl flex items-center gap-3">
                      <MapPin className="w-8 h-8 text-amber-600" />
                      UAE Emirates We Serve
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {uaeCities.map((city, index) => {
                        const Icon = city.icon;
                        return (
                          <div key={index} className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                            <Icon className="w-6 h-6 text-amber-600" />
                            <span className="font-semibold text-gray-800">{city.name}</span>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-sm text-gray-600 mt-6">
                      We deliver to all areas across the UAE including free zones, residential communities, and business districts.
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Quote Form */}
        <section id="quote-form" className="py-20 bg-gradient-to-br from-amber-600 via-orange-600 to-yellow-600">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Card className="shadow-2xl">
                <CardHeader>
                  <CardTitle className="text-3xl text-center">Get Free Shipping Quote to UAE</CardTitle>
                  <p className="text-center text-gray-600 mt-2">We'll respond within 24 hours with competitive rates</p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Your Name *</label>
                      <Input 
                        name="name" 
                        value={formData.name} 
                        onChange={handleChange} 
                        required 
                        placeholder="John Doe" 
                        className="h-12"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                      <Input 
                        type="email" 
                        name="email" 
                        value={formData.email} 
                        onChange={handleChange} 
                        required 
                        placeholder="john@example.com" 
                        className="h-12"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                      <Input 
                        type="tel" 
                        name="phone" 
                        value={formData.phone} 
                        onChange={handleChange} 
                        required 
                        placeholder="+1 (123) 456-7890" 
                        className="h-12"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Approximate Weight *</label>
                      <Input 
                        name="weight" 
                        value={formData.weight} 
                        onChange={handleChange} 
                        required 
                        placeholder="e.g., 5 kg" 
                        className="h-12"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white h-14 text-lg font-semibold shadow-xl"
                    >
                      Get Quote Now
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gray-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Need Help with Your UAE Shipment?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Our team is ready to assist with customs, documentation, and shipping guidance
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                className="bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-600 hover:to-amber-700 text-white px-8 py-6 text-lg"
                onClick={() => window.location.href = '/contact'}
              >
                Contact Shippzy
              </Button>
              <Button 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
                onClick={() => window.location.href = '/track-shipment'}
              >
                Track Shipment
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ShippingCanadaToUAEPage;
